"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type ProducerOption = {
  id: number;
  name?: string | null;
};

function displayProducerName(p: ProducerOption) {
  const n = p.name?.trim();
  return n || `Producer #${p.id}`;
}

type ProducerComboboxProps = {
  producers: ProducerOption[];
  value: string;
  onValueChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  "aria-invalid"?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
};

export function ProducerCombobox({
  producers,
  value,
  onValueChange,
  id,
  disabled,
  "aria-invalid": ariaInvalid,
  placeholder = "Select a producer…",
  searchPlaceholder = "Search producer…",
}: ProducerComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const selected = value
    ? producers.find((p) => String(p.id) === value)
    : undefined;
  const summary = selected ? displayProducerName(selected) : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          className="h-auto min-h-9 w-full justify-between py-2 font-normal"
        >
          <span className="min-w-0 flex-1 truncate text-left">{summary}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>No producer found.</CommandEmpty>
            <CommandGroup>
              {producers.map((p) => {
                const idStr = String(p.id);
                const name = displayProducerName(p);
                return (
                  <CommandItem
                    key={p.id}
                    value={`${idStr} ${name}`}
                    keywords={[
                      name,
                      idStr,
                      p.name?.trim() || "",
                      name.toLowerCase(),
                    ]}
                    onSelect={() => {
                      onValueChange(idStr);
                      setOpen(false);
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">{name}</span>
                    <Check
                      className={cn(
                        "ml-auto size-4 shrink-0",
                        value === idStr ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
